(function () {
  'use strict';
  const validId = value => /^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value));
  const validCount = value => Number.isSafeInteger(value) && value >= 0;
  function purchaseItems(items) {
    if (!Array.isArray(items) || !items.length || items.length > 20) throw new Error('Choose an item to add.');
    return items.map(item => {
      if (!validId(item.id) || !validCount(item.quantity) || item.quantity < 1 || item.quantity > 99) throw new Error('Review the item quantity.');
      if (item.selling_plan != null && item.selling_plan !== '') throw new Error('This offer supports one-time purchases only.');
      const properties = {};
      if (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties))) throw new Error('Review the vehicle details.');
      for (const [key, value] of Object.entries(item.properties || {})) {
        if (!key || key.length > 100 || typeof value !== 'string' || value.length > 500) throw new Error('Review the vehicle details.');
        if (value) properties[key] = value;
      }
      return { id: Number(item.id), quantity: item.quantity, ...(Object.keys(properties).length ? { properties } : {}) };
    });
  }
  function validCart(cart, currency) {
    if (!cart || !Array.isArray(cart.items) || !validCount(cart.item_count) || (currency && cart.currency !== currency)) return false;
    const keys = new Set(); let count = 0;
    for (const line of cart.items) {
      if (typeof line.key !== 'string' || !line.key.trim() || keys.has(line.key) || !validId(line.variant_id || line.id) || !validCount(line.quantity) || line.quantity < 1) return false;
      keys.add(line.key); count += line.quantity;
    }
    return validCount(count) && count === cart.item_count;
  }
  function createClient(environment = {}) {
    const request = environment.fetch || ((...args) => globalThis.fetch(...args));
    const browser = environment.window || globalThis.window;
    const storage = environment.storage || globalThis.sessionStorage;
    const document = environment.document || globalThis.document;
    const Event = environment.CustomEvent || globalThis.CustomEvent;
    const pendingKey = 'sac-liqui-moly-cart-pending-v2';
    let root = environment.root || '/', currency = environment.currency || '', tail = Promise.resolve();
    const state = { busy: false, uncertain: false, cart: null };
    try { state.uncertain = storage?.getItem(pendingKey) === '1'; } catch {}
    function configure(options = {}) {
      if (typeof options.root === 'string' && /^\/(?!\/)[^?#]*$/.test(options.root)) root = options.root.replace(/\/?$/, '/');
      if (typeof options.currency === 'string' && /^[A-Z]{3}$/.test(options.currency)) currency = options.currency;
      return api;
    }
    function emit(name, detail) { if (document?.dispatchEvent && Event) document.dispatchEvent(new Event(name, { detail })); }
    function setPending(value) { try { if (value) storage?.setItem(pendingKey, '1'); else storage?.removeItem(pendingKey); } catch {} }
    function applyBadge(sections) {
      const html = sections?.['cart-icon-bubble'], bubble = document?.getElementById?.('cart-icon-bubble');
      if (bubble && typeof html === 'string' && html.length < 50000) bubble.innerHTML = html;
    }
    function publish(cart, source) {
      state.cart = cart;
      const bubble = document?.getElementById?.('cart-icon-bubble');
      if (bubble) {
        const count = bubble.querySelector?.('.header__cart-count [aria-hidden="true"]');
        if (count) count.textContent = String(cart.item_count);
        else if (!bubble.childElementCount) bubble.textContent = String(cart.item_count);
      }
      emit('sac:liqui-cart:updated', { cart, source, uncertain: state.uncertain });
    }
    function stateChanged() { emit('sac:liqui-cart:state', { busy: state.busy, uncertain: state.uncertain }); }
    async function refreshDrawer(cart, open) {
      let timer, cancelWait = () => {};
      const timeout = new Promise((resolve, reject) => { timer = setTimeout(() => reject(new Error('CART_DISPLAY_TIMEOUT')), environment.drawerTimeoutMs || 6000); });
      const publicCart = () => { try { return browser?.upcartGetCart?.(); } catch { return null; } };
      const matches = value => {
        if (!value || !Array.isArray(value.items) || value.currency !== cart.currency || value.total !== cart.total_price || value.items.length !== cart.items.length) return false;
        const lines = new Map(value.items.map(line => [line.key, line]));
        return lines.size === cart.items.length && cart.items.every(line => {
          const visible = lines.get(line.key);
          return visible && String(visible.variantId) === String(line.variant_id || line.id) && visible.quantity === line.quantity && visible.finalLinePrice === line.final_line_price;
        });
      };
      const waitForCart = predicate => new Promise(resolve => {
        let task;
        const events = ['aftersell-upcart:public-events:cart-loaded', 'aftersell-upcart:public-events:cart-updated'];
        const cleanup = () => { clearTimeout(task); events.forEach(name => browser?.removeEventListener?.(name, check)); };
        const check = () => {
          clearTimeout(task);
          task = setTimeout(() => { if (predicate(publicCart())) { cleanup(); resolve(); } }, 0);
        };
        cancelWait = cleanup;
        events.forEach(name => browser?.addEventListener?.(name, check));
        check();
      });
      try {
        if (typeof browser?.upcartRefreshCart === 'function') {
          await Promise.race([waitForCart(value => value && Array.isArray(value.items)), timeout]);
          await Promise.race([Promise.resolve().then(() => browser.upcartRefreshCart()), timeout]);
          await Promise.race([waitForCart(matches), timeout]);
          if (open) {
            if (typeof browser.upcartOpenCart !== 'function') throw new Error('CART_DISPLAY_UNAVAILABLE');
            if (!document?.body?.classList?.contains('upcartPopupShow')) browser.upcartOpenCart();
          }
        } else {
          const drawer = document?.querySelector?.('cart-drawer');
          if (drawer?.refresh) {
            await Promise.race([Promise.resolve().then(() => drawer.refresh(true)), timeout]);
            if (open) drawer.open?.();
          } else if (open) browser?.location?.assign?.(root + 'cart');
        }
        return true;
      } catch {
        if (open) { try { browser?.location?.assign?.(root + 'cart'); } catch {} }
        return false;
      } finally { clearTimeout(timer); cancelWait(); }
    }
    async function json(path, options = {}) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), environment.timeoutMs || 18000);
      try {
        const response = await request(root + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        const value = await response.json();
        if (!response.ok) { const error = new Error(typeof value.description === 'string' ? value.description : 'Your cart could not be updated.'); error.httpStatus = response.status; throw error; }
        return value;
      } finally { clearTimeout(timer); }
    }
    async function fresh(source) {
      const cart = await json('cart.js');
      if (!validCart(cart, currency)) throw new Error('Your cart could not be checked.');
      publish(cart, source);
      // Native section HTML retains the theme's badge wrapper and translated accessible label.
      const bubble = document?.getElementById?.('cart-icon-bubble');
      if (bubble?.querySelector) { try { applyBadge(await json('?sections=cart-icon-bubble')); } catch {} }
      return cart;
    }
    function enqueue(task) { const next = tail.then(task, task); tail = next.catch(() => {}); return next; }
    function read({ source = 'lm-read' } = {}) { return enqueue(() => fresh(source)); }
    function acknowledge({ source = 'lm-review' } = {}) {
      return enqueue(async () => { const cart = await fresh(source); state.uncertain = false; setPending(false); stateChanged(); publish(cart, source); return cart; });
    }
    function mutate(kind, options) {
      return enqueue(async () => {
        if (state.uncertain) { const error = new Error('Review your cart before making another change.'); error.uncertain = true; throw error; }
        state.busy = true; stateChanged();
        let posted = false, payload, before;
        const source = options.source || 'lm-cart';
        try {
          before = await fresh(source);
          if (kind === 'add') payload = { items: purchaseItems(options.prepare ? await options.prepare(before) : options.items) };
          else {
            const line = before.items.find(item => item.key === options.key);
            if (!line || line.selling_plan_allocation || line.gift_card) { const error = new Error('Your cart changed. Please review it and try again.'); error.code = 'STALE_LINE'; throw error; }
            const count = Number.isInteger(options.delta) ? line.quantity + options.delta : options.quantity;
            if (!validCount(count) || count > 99) throw new Error('Review the item quantity.');
            payload = { id: line.key, quantity: count };
          }
          payload.sections = ['cart-icon-bubble'];
          setPending(true); posted = true;
          const result = await json('cart/' + kind + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
          applyBadge(result.sections);
          const cart = await fresh(source);
          setPending(false);
          await refreshDrawer(cart, options.openCart === true);
          if (kind === 'add') emit('on:cart:add', { variantId: payload.items[0].id, cart, source: 'sac-liqui-moly' });
          else { const old = before.items.find(line => line.key === options.key); emit('on:line-item:change', { variantId: old.variant_id, oldQuantity: old.quantity, newQuantity: payload.quantity, cart, source: 'sac-liqui-moly' }); }
          return cart;
        } catch (error) {
          if (posted) {
            // A rejected or interrupted add can still have changed Shopify's cart. Never replay it.
            state.uncertain = true; error.uncertain = true;
            try { await refreshDrawer(await fresh(source), false); } catch {}
          }
          throw error;
        } finally { state.busy = false; stateChanged(); }
      });
    }
    const api = { state, configure, read, acknowledge, add: options => mutate('add', options), change: options => mutate('change', options) };
    configure(environment);
    return api;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { createClient, purchaseItems, validCart };
  if (typeof window !== 'undefined' && !window.SacLmCart) window.SacLmCart = createClient({ root: window.Shopify?.routes?.root || '/' });
}());

